import {
    createPagePagingActions,
    Field,
    FinalFormInput,
    FinalFormSwitch,
    SortDirection,
    StackSwitchApiContext,
    Table as AdminTable,
    TableFilterFinalForm,
    TableQuery,
    useTableQuery,
    useTableQueryFilter,
    useTableQueryPaging,
    useTableQuerySort,
    VisibleType,
} from "@comet/admin";
import { ErrorOutline } from "@mui/icons-material";
import { FormControlLabel, Grid, IconButton, Tooltip } from "@mui/material";
import { ContentWrapper, formatDateTimeAsString, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import { ResetButton } from "@src/components/ResetButton";
import CountrySelect from "@src/components/select/CountrySelect";
import { StyledFilter, StyledSearch, TableFilter } from "@src/components/table/TableStyles";
// eslint-disable-next-line @comet/no-private-sibling-import
import { camerasQuery } from "@src/domain/devices/Table.gql";
import { GQLCameraConfigurationStatus, type GQLCamerasAdminQuery, type GQLCamerasAdminQueryVariables, GQLCameraStatus } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import Warning from "@src/icons/Warning";
import { type ExportVariables, loadLimit } from "@src/utils";
import { CSVExportButton } from "@src/utils/CSVExportButton";
import { useCSVExportPagedTableQuery } from "@src/utils/usePagedCSVExport";
import { isBefore, subHours } from "date-fns";
import { type FC, type PropsWithChildren, useCallback, useContext } from "react";
import { useIntl } from "react-intl";

import { CameraStatus } from "./CameraStatus";
import { getCameraStatusValue } from "./edit/Form";
import { StyledDeviceLink, SwitchContainer } from "./Table.sc";

interface TableProps {
    persistedStateId?: string;
}

const Table: FC<PropsWithChildren<TableProps>> = ({ persistedStateId }) => {
    const pagingApi = useTableQueryPaging(1, { persistedStateId });
    const stackApi = useContext(StackSwitchApiContext);
    const sortApi = useTableQuerySort({ columnName: "camera.createdAt", direction: SortDirection.DESC }, { persistedStateId });
    const filterApi = useTableQueryFilter<GQLCamerasAdminQueryVariables>({}, { pagingApi, persistedStateId });
    const intl = useIntl();

    const { status, country, query, isRoamingSim } = filterApi.current;
    const addAdditionalQueryVariablesFn = useCallback(() => ({ status, country, query, isRoamingSim }), [country, query, status, isRoamingSim]);

    const memoizedVariables = useMemoizedTableVariables<GQLCamerasAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLCamerasAdminQuery, GQLCamerasAdminQueryVariables>()(camerasQuery, {
        resolveTableData: ({ camerasAdmin }) => ({
            data: camerasAdmin.nodes,
            totalCount: camerasAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, camerasAdmin),
        }),
        variables: memoizedVariables,
    });

    const statusOptions = [
        { label: intl.formatMessage({ id: "reviermanager.devices.filter.status.activated", defaultMessage: "Ja" }), value: GQLCameraStatus.Active },
        {
            label: intl.formatMessage({ id: "reviermanager.devices.filter.status.notActivated", defaultMessage: "Nein" }),
            value: GQLCameraStatus.Sleep,
        },
        { label: intl.formatMessage({ id: "reviermanager.devices.filter.status.defect", defaultMessage: "Defekt" }), value: GQLCameraStatus.Defect },
    ];

    const handleFilterReset = () => {
        filterApi.formApi.reset({});
    };

    const fileName = `${intl.formatMessage({
        id: "reviermanager.common.export.devices",
        defaultMessage: "Geräte",
    })}`;

    const exportApi = useCSVExportPagedTableQuery<ExportVariables>(
        api,
        {
            fromPage: 1,
            toPage: tableData?.totalCount ? Math.ceil(tableData.totalCount / loadLimit) : 1,
            variablesForPage: (page) => {
                return {
                    ...memoizedVariables,
                    page,
                    size: loadLimit,
                };
            },
        },
        fileName,
    );

    const getLastActivityString = (lastActivity: Date) => {
        if (!lastActivity) {
            return null;
        }
        const dateElement = <FormattedDateWidthDateTime value={lastActivity} />;
        if (isBefore(lastActivity, subHours(new Date(), 36))) {
            return (
                <>
                    <Warning /> {dateElement}
                </>
            );
        }
        return dateElement;
    };

    return (
        <ContentWrapper marginVariant="table">
            <TableFilterFinalForm filterApi={filterApi}>
                <TableFilter container>
                    <Grid size={1}>
                        <StyledFilter>
                            <StaticSelectField
                                name="country"
                                label={intl.formatMessage({ id: "reviermanager.devices.filter.country", defaultMessage: "Land" })}
                                component={CountrySelect}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={1}>
                        <StyledFilter>
                            <StaticSelectField
                                name="status"
                                label={intl.formatMessage({
                                    id: "reviermanager.devices.filter.status",
                                    defaultMessage: "Aktiviert",
                                })}
                                options={statusOptions}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.filter.all",
                                    defaultMessage: "Alle",
                                })}
                                isClearable
                                variant="vertical"
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={3}>
                        <StyledFilter>
                            <Field
                                type="text"
                                label={intl.formatMessage({ id: "reviermanager.devices.filter.fulltext", defaultMessage: "Suche" })}
                                name="query"
                                component={FinalFormInput}
                                placeholder={intl.formatMessage({
                                    id: "reviermanager.devices.filter.fulltext.placeholder",
                                    defaultMessage: "IMEI, SN, MSISDN, ...",
                                })}
                                startAdornment={<StyledSearch />}
                                variant="vertical"
                                fullWidth
                            />
                        </StyledFilter>
                    </Grid>
                    <Grid size={2}>
                        <SwitchContainer>
                            <Field name="isRoamingSim" type="checkbox">
                                {(props) => (
                                    <FormControlLabel
                                        label={intl.formatMessage({
                                            id: "reviermanager.devices.filter.isRoamingSim",
                                            defaultMessage: "MKL Sim-Karte",
                                        })}
                                        control={<FinalFormSwitch {...props} />}
                                    />
                                )}
                            </Field>
                        </SwitchContainer>
                    </Grid>
                    {Object.keys(filterApi.current).length > 0 && (
                        <Grid>
                            <ResetButton onClick={handleFilterReset} />
                        </Grid>
                    )}
                </TableFilter>
            </TableFilterFinalForm>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        exportApis={[exportApi]}
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.devices",
                                defaultMessage: "{count, plural, one {Gerät} other {Geräte}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "camera.name",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.camera.name", defaultMessage: "Name" }),
                                render: ({ name, id }) => (
                                    <StyledDeviceLink
                                        onClick={() => {
                                            stackApi.activatePage("edit", String(id));
                                        }}
                                    >
                                        {name}
                                    </StyledDeviceLink>
                                ),
                                renderExcel: ({ name }) => name,
                            },
                            {
                                name: "camera.country",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.camera.country", defaultMessage: "Land" }),
                                render: ({ country }) => country,
                            },
                            {
                                name: "imei",
                                header: intl.formatMessage({ id: "reviermanager.camera.imei", defaultMessage: "IMEI" }),
                                render: ({ hardwareInfo }) => hardwareInfo.imei,
                            },
                            {
                                name: "sn",
                                header: intl.formatMessage({ id: "reviermanager.camera.sn", defaultMessage: "SN" }),
                                render: ({ hardwareInfo }) => hardwareInfo.sn,
                            },
                            {
                                name: "msisdn",
                                header: intl.formatMessage({ id: "reviermanager.camera.msisdn", defaultMessage: "MSISDN" }),
                                render: ({ hardwareInfo }) => hardwareInfo.msisdn,
                            },
                            {
                                name: "camera.status",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.camera.status", defaultMessage: "Aktiviert" }),
                                render: ({ status, configurationStatus }) => {
                                    const hasConfigurationError = configurationStatus === GQLCameraConfigurationStatus.Error;

                                    if (hasConfigurationError) {
                                        return (
                                            <Tooltip
                                                title={intl.formatMessage({
                                                    id: "reviermanager.camera.table.status.tooltip.configurationError",
                                                    defaultMessage: "Konfigurationsfehler",
                                                })}
                                            >
                                                <ErrorOutline fontSize="large" color="error" />
                                            </Tooltip>
                                        );
                                    }

                                    const title = getCameraStatusValue(status, intl);
                                    return (
                                        <Tooltip title={title}>
                                            <div>
                                                <CameraStatus status={status} />
                                            </div>
                                        </Tooltip>
                                    );
                                },
                                renderExcel: ({ status, configurationStatus }) => {
                                    const hasConfigurationError = configurationStatus === GQLCameraConfigurationStatus.Error;

                                    if (hasConfigurationError) {
                                        return intl.formatMessage({
                                            id: "reviermanager.camera.table.status.tooltip.configurationError",
                                            defaultMessage: "Konfigurationsfehler",
                                        });
                                    }

                                    return getCameraStatusValue(status, intl);
                                },
                            },
                            {
                                name: "camera.activatedAt",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.camera.table.activatedAt", defaultMessage: "aktiviert am" }),
                                render: ({ activatedAt }) => activatedAt && <FormattedDateWidthDateTime value={activatedAt} />,
                                renderExcel: ({ activatedAt }) => formatDateTimeAsString(activatedAt, intl),
                            },
                            {
                                name: "camera.lastActivity",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.camera.table.lastActivity", defaultMessage: "letzte Aktivität" }),
                                render: ({ lastActivity }) => getLastActivityString(lastActivity),
                                renderExcel: ({ lastActivity }) => lastActivity && formatDateTimeAsString(lastActivity, intl),
                            },
                            {
                                name: "actions",
                                visible: { [VisibleType.Browser]: true, [VisibleType.Export]: false },
                                render: ({ id }) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            stackApi.activatePage("edit", String(id));
                                        }}
                                    >
                                        <ArrowRight color="primary" />
                                    </IconButton>
                                ),
                            },
                        ]}
                    />
                )}
            </TableQuery>
            {tableData && <CSVExportButton exportApi={exportApi} />}
        </ContentWrapper>
    );
};

export default Table;
